import { autorun, computed, observable, toJS, runInAction } from 'mobx';
import SsoClient from '@teliaee/telia-sso-client';
import config from '../Config';
import ErrorUtil from '../error/ErrorUtil';
import { CommonAPI } from '../util/ApiUtil';
import CookieApi from '../util/CookieApi';
import { safeReaction } from '../util/MobxUtil';
import LanguageStore, { languageSlug } from './LanguageStore';
import RootStore from './RootStore';
import RouteStore from './RouteStore';
import SegmentStore, { SEGMENT_PRIVATE } from './SegmentStore';

export type CustomerSegmentType = 'B2C' | 'B2B';
export const CUSTOMER_TYPE_PRIVATE = 'private';
export const CUSTOMER_TYPE_BUSINESS = 'business';
const AUTH_TYPE_DEALER = 'dealerService';

export interface AuthMandate {
  mandate: string;
  elionAccounts?: number[];
  emtProducts?: number[];
  diilAccounts?: number[];
}

export interface AuthPerson {
  displayName: string;
  firstName: string;
  lastName: string;
  personalCode?: string;
  personalCodeCountryCode?: string;
  profileId: string;
  profileAlias: string[];
}

export interface AuthCustomer {
  customerId: number;
  elionCustomerId?: number;
  displayName: string;
  type: string;
  registrationNumber: string;
  registrationNumberCountryCode: string;
}

export interface AuthDealer {
  userName: string;
  displayName: string;
  title: string;
  department: string;
  roles: string[];
}

export interface Auth {
  authType: string;
  authLevel: number;
  person: AuthPerson;
  customer: AuthCustomer;
  dealer: AuthDealer;
  mandates: AuthMandate[];
}

export interface SsoResultModel {
  message?: string;
  action: string;
  auth?: Auth;
  token?: string;
  am?: string;
  warning?: string;
  error?: string;
  goto?: string;
}

class SsoStore {
  @observable accessor keepSsoFrameOpenAfterLoginOnHomepage: boolean = false;
  @observable accessor goToUrl: string = null;
  @observable accessor auth: Auth;
  @observable accessor errors: string[] = [];
  @observable accessor isEmployee: boolean = false;
  @observable accessor initialSsoMessageReceived: boolean = false; // only public for test
  @observable accessor isSsoModalOpen = false;
  @observable accessor clientState: SsoResultModel;
  @observable accessor selectMandate: boolean;

  private languageStore: LanguageStore;
  private routeStore: RouteStore;
  private segmentStore: SegmentStore;
  private sso: SsoClient;

  private redirectUrl: string;
  private loginCallback: () => void;
  private redirectIfDealer: boolean = false;

  public constructor(rootStore: RootStore) {
    this.languageStore = rootStore.languageStore;
    this.routeStore = rootStore.routeStore;
    this.segmentStore = rootStore.segmentStore;

    runInAction(() => {
      this.sso = SsoClient.connect(
        {
          onLogout: this.handleLogout,
          onLogin: this.handleLogin
        },
        { locale: this.languageStore.currentLanguage }
      );
    });

    this.sso.loadInitialState();

    safeReaction(
      rootStore,
      () => this.languageStore.currentLanguage,
      (lang) => this.sso.setOptions({ locale: lang })
    );

    autorun(() => (window.user = { isLoggedIn: this.loggedIn }));
  }

  @computed
  get loggedIn() {
    return !!this.auth && !!this.auth.person && !!this.auth.authLevel;
  }

  @computed
  get personAndCustomerInfo() {
    return this.auth;
  }

  @computed
  get person() {
    return this.auth && this.auth.person;
  }

  @computed
  get customer() {
    return this.auth && this.auth.customer;
  }

  @computed
  get dealer() {
    return this.auth && this.auth.dealer;
  }

  @computed
  get ssoFrameVisible() {
    return this.sso && this.isSsoModalOpen;
  }

  public openSsoFrame = () => {
    runInAction(() => {
      this.isSsoModalOpen = true;
    });
  };

  @computed
  get customerId() {
    return this.customer && this.customer.customerId;
  }

  @computed
  get isDealer() {
    return this.auth && this.auth.authType === AUTH_TYPE_DEALER;
  }

  @computed
  get personalCode() {
    return this.person && this.person.personalCode;
  }

  @computed
  get loginOptions() {
    return {
      selectMandate: this.selectMandate === undefined ? true : this.selectMandate,
      locale: this.languageStore.currentLanguage,
      authLevel: 2,
      goto: this.goToUrl
    };
  }

  get ssoClientState() {
    return toJS(this.clientState);
  }

  @computed
  get ssoClient() {
    return this.sso;
  }

  @computed
  get customerType() {
    if (this.auth && this.auth.customer) {
      return this.auth.customer.type === 'B2B' ? CUSTOMER_TYPE_BUSINESS : CUSTOMER_TYPE_PRIVATE;
    }
    const headerSegmentCookie = CookieApi.getCookieValue('segment');
    if (headerSegmentCookie) {
      return headerSegmentCookie.toUpperCase() === 'B2B' ? CUSTOMER_TYPE_BUSINESS : CUSTOMER_TYPE_PRIVATE;
    }
    return CUSTOMER_TYPE_PRIVATE;
  }

  @computed
  public get customerSegmentType(): CustomerSegmentType {
    return this.customerType === 'business' ? 'B2B' : 'B2C';
  }

  @computed
  get isLoggedInWithHardAuth(): boolean {
    return this.auth?.authLevel === 2 || (!!this.dealer && !!this.customerId);
  }

  public get hasLegalOrServiceTeliaMandate(): boolean {
    return (
      (this.hasMandate('LEGAL') || this.hasMandate('SERVICE')) &&
      this.auth?.mandates?.some((m: AuthMandate) => m.elionAccounts?.length || m.emtProducts?.length)
    );
  }

  private hasMandate(mandate: string): boolean {
    return this.auth?.mandates?.some((m: AuthMandate) => m.mandate === mandate);
  }

  public ensureLogin = (redirectUrl?: string, callback?: () => void, selectMandate?: boolean) => {
    runInAction(() => {
      this.sso.setOptions({ authLevel: 2 });

      if (this.loggedIn && !this.redirectUrl && selectMandate === undefined) {
        this.routeStore.showSpinner();
      }
      if (!this.loggedIn) {
        this.setLoginParameters(redirectUrl, callback, redirectUrl, selectMandate);
      } else if (redirectUrl) {
        this.routeStore.push(redirectUrl);
      } else if (callback) {
        callback();
        this.routeStore.hideSpinner();
      }
    });
  };

  public ensureLoginWithGotoUrl = (goToUrl: string) => {
    runInAction(() => {
      this.sso.setOptions({ authLevel: 2 });

      if (!this.loggedIn) {
        this.goToUrl = goToUrl;
        this.openSsoFrame();
      } else {
        this.routeStore.push(goToUrl);
      }
    });
  };

  public ensureLoginOrDealerWithCallback = (callback: () => void) => {
    runInAction(() => {
      if (!this.loggedIn && !this.isDealer) {
        this.setLoginParameters(null, callback);
      } else if (callback) {
        this.routeStore.showSpinner();
        callback();
      }
    });
  };

  @computed
  get isSsoLoaded(): boolean {
    return !!this.initialSsoMessageReceived;
  }

  private handleLogin = (ssoResult: SsoResultModel) => {
    runInAction(() => {
      this.clientState = ssoResult;
      this.initialSsoMessageReceived = true;
      this.auth = ssoResult.auth;
      if (this.ssoFrameClosingRequired()) {
        this.isSsoModalOpen = false;
      }

      if (this.redirectIfDealer && this.isDealer) {
        this.routeStore.push(config.teliaWebSelfServiceUrl + languageSlug(this.languageStore.currentLanguage));
      }

      if (this.loggedIn && this.redirectUrl) {
        const url = this.redirectUrl;
        this.redirectUrl = null;
        this.goToUrl = null;
        this.routeStore.push(url);
      }

      if (this.loggedIn && this.loginCallback && typeof this.loginCallback === 'function') {
        this.loginCallback();
        this.loginCallback = null;
      }

      if (this.loggedIn && this.goToUrl) {
        this.goToUrl = null;
        if (ssoResult.goto) {
          this.routeStore.push(ssoResult.goto);
        }
      }

      this.redirectIfDealer = false;
    });
  };

  private ssoFrameClosingRequired = () => {
    if (!this.loggedIn) {
      return this.isDealer;
    }
    if (this.redirectUrl) {
      return true; // that has own spinner
    }

    return !(this.keepSsoFrameOpenAfterLoginOnHomepage && this.routeStore.currentPathIsHomepage);
  };

  public setKeepSsoFrameOpenAfterLoginOnHomepage = () => {
    this.keepSsoFrameOpenAfterLoginOnHomepage = true;
  };

  public fetchIsEmployee = async () => {
    try {
      if (this.customerId && this.segmentStore.currentSegment === SEGMENT_PRIVATE) {
        const response = await CommonAPI.get('/mandate/is-employee');
        runInAction(() => {
          this.isEmployee = response.data;
        });
      } else {
        runInAction(() => {
          this.isEmployee = false;
        });
      }
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
      runInAction(() => {
        this.isEmployee = false;
      });
    }
  };

  public handleLogout = () => {
    runInAction(() => {
      this.clientState = { action: 'logout' };
      this.initialSsoMessageReceived = true;
      this.auth = null;
      this.redirectUrl = null;
      this.goToUrl = null;
      this.isSsoModalOpen = false;
    });
  };

  private setLoginParameters = (redirectUrl?: string, loginCallback?: () => void, gotoUrl: string = null, selectMandate: boolean = true) => {
    runInAction(() => {
      this.selectMandate = selectMandate;
      this.goToUrl = gotoUrl;
      this.openSsoFrame();
      this.redirectUrl = redirectUrl;
      this.loginCallback = loginCallback;
      this.redirectIfDealer = true;
    });
  };

  public closeSsoFrame = () => {
    runInAction(() => {
      this.sso.setOptions({ authLevel: 0 });
      this.goToUrl = null;
      this.isSsoModalOpen = false;
      this.redirectUrl = null;
      this.loginCallback = null;
      this.redirectIfDealer = false;
      this.routeStore.showingSpinner = false;
    });
  };
}

export default SsoStore;
